import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const HomePageOne = () => {
    const { t } = useTranslation();
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.s7even.software.catalogo";

    return (
        <div className="bg-white min-vh-100 d-flex flex-column font-sans">
            {/* Header / Navbar */}
            <header className="py-16 border-bottom sticky-top bg-white shadow-2xs z-3">
                <div className="container container-lg d-flex align-items-center justify-content-between">
                    <Link to="/" className="d-flex align-items-center gap-12 text-decoration-none">
                        <img src="/logo192.png" alt="Catálogo App" className="w-40 h-40 object-fit-contain" />
                        <span className="fw-bold text-gray-900 text-xl tracking-tight">Catálogo App</span>
                    </Link>

                    <div className="d-flex align-items-center gap-16">
                        <LanguageSwitcher />

                        <a 
                            href={playStoreUrl}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-outline-main rounded-pill px-20 py-8 text-sm fw-semibold d-none d-sm-inline-flex flex-align gap-8"
                        >
                            <i className="ph ph-android-logo text-lg"></i>
                            {t('landing.header.appAndroid')}
                        </a>

                        <Link 
                            to="/admin/login" 
                            className="btn btn-main rounded-pill px-24 py-8 text-sm fw-bold flex-align gap-8 shadow-xs"
                        >
                            <i className="ph ph-user text-lg"></i>
                            <span className="d-none d-md-inline">{t('landing.header.vendorLogin')}</span>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-60 py-lg-80 bg-main-25 position-relative overflow-hidden">
                <div className="container container-lg">
                    <div className="row align-items-center g-40">
                        <div className="col-lg-7">
                            <span className="badge bg-main-100 text-main-600 px-16 py-8 rounded-pill text-xs fw-bold mb-16 d-inline-flex flex-align gap-6">
                                <i className="ph ph-sparkle text-sm"></i>
                                {t('landing.hero.badge')}
                            </span>
                            
                            <h1 className="fw-extrabold text-gray-900 display-5 mb-20 lh-sm">
                                {t('landing.hero.titlePrefix')}<span className="text-main-600">{t('landing.hero.titleHighlight')}</span>
                            </h1>

                            <p className="text-gray-600 text-lg mb-32 lh-lg max-w-600">
                                {t('landing.hero.subtitle')}
                            </p>

                            <div className="d-flex align-items-center gap-16 flex-wrap">
                                <Link 
                                    to="/admin/login" 
                                    className="btn btn-main rounded-pill px-32 py-14 fw-bold text-md flex-align gap-10 shadow-sm"
                                >
                                    <i className="ph ph-sign-in text-xl"></i>
                                    {t('landing.hero.btnStart')}
                                </Link>

                                <a 
                                    href={playStoreUrl}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn btn-dark bg-gray-900 text-white rounded-pill px-24 py-14 fw-semibold text-md flex-align gap-10 hover-bg-gray-800 transition-2"
                                >
                                    <i className="ph ph-google-play-logo text-xl text-success"></i>
                                    {t('landing.hero.btnPlayStore')}
                                </a>
                            </div>
                        </div>

                        <div className="col-lg-5 text-center">
                            <div className="position-relative d-inline-block">
                                <div className="p-24 bg-white rounded-24 border shadow-md">
                                    <img 
                                        src="/logo512.png" 
                                        alt="Catálogo App Mockup" 
                                        className="w-100 max-w-320 rounded-16 object-fit-contain" 
                                    />
                                    <div className="mt-20 pt-16 border-top">
                                        <span className="text-xs text-gray-400 d-block mb-4">{t('landing.hero.mockupAvailable')}</span>
                                        <h6 className="fw-bold text-gray-900 mb-0">{t('landing.hero.mockupTitle')}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Info Section: What is Catálogo App */}
            <section className="py-60 py-lg-80">
                <div className="container container-lg">
                    <div className="text-center max-w-700 mx-auto mb-56">
                        <span className="text-2xs text-main-600 text-uppercase fw-bold tracking-wider d-block mb-8">
                            {t('landing.info.whatIsLabel')}
                        </span>
                        <h2 className="fw-bold text-gray-900">
                            {t('landing.info.title')}
                        </h2>
                        <p className="text-gray-500 mt-12 text-md">
                            {t('landing.info.subtitle')}
                        </p>
                    </div>

                    <div className="row g-24">
                        <div className="col-md-4">
                            <div className="p-32 rounded-24 bg-gray-50 border h-100 hover-border-main-600 transition-2">
                                <div className="w-56 h-56 rounded-16 bg-main-100 text-main-600 flex-center mb-24 text-2xl">
                                    <i className="ph ph-storefront"></i>
                                </div>
                                <h5 className="fw-bold text-gray-900 mb-12">{t('landing.info.feature1Title')}</h5>
                                <p className="text-gray-600 text-sm mb-0 lh-md">
                                    {t('landing.info.feature1Desc')}
                                </p>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="p-32 rounded-24 bg-gray-50 border h-100 hover-border-main-600 transition-2">
                                <div className="w-56 h-56 rounded-16 bg-success-100 text-success flex-center mb-24 text-2xl">
                                    <i className="ph ph-whatsapp-logo"></i>
                                </div>
                                <h5 className="fw-bold text-gray-900 mb-12">{t('landing.info.feature2Title')}</h5>
                                <p className="text-gray-600 text-sm mb-0 lh-md">
                                    {t('landing.info.feature2Desc')}
                                </p>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="p-32 rounded-24 bg-gray-50 border h-100 hover-border-main-600 transition-2">
                                <div className="w-56 h-56 rounded-16 bg-warning-100 text-warning flex-center mb-24 text-2xl">
                                    <i className="ph ph-palette"></i>
                                </div>
                                <h5 className="fw-bold text-gray-900 mb-12">{t('landing.info.feature3Title')}</h5>
                                <p className="text-gray-600 text-sm mb-0 lh-md">
                                    {t('landing.info.feature3Desc')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Android App Download Banner */}
            <section className="py-60 bg-gray-900 text-white">
                <div className="container container-lg">
                    <div className="row align-items-center justify-content-between g-32">
                        <div className="col-lg-7">
                            <div className="d-flex align-items-center gap-16 mb-16">
                                <i className="ph ph-android-logo text-4xl text-success"></i>
                                <h3 className="fw-bold text-white mb-0">{t('landing.download.title')}</h3>
                            </div>
                            <p className="text-gray-300 text-md mb-0 max-w-600">
                                {t('landing.download.desc')}
                            </p>
                        </div>
                        <div className="col-lg-5 text-lg-end">
                            <a 
                                href={playStoreUrl}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="d-inline-block hover-opacity-90 transition-2"
                            >
                                <img 
                                    src="https://play.google.com/intl/en_us/badges/static/images/badges/es_badge_web_generic.png" 
                                    alt={t('landing.download.badgeAlt')} 
                                    className="h-64 object-fit-contain" 
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="mt-auto bg-gray-950 text-gray-400 py-48 border-top border-gray-800">
                <div className="container container-lg">
                    <div className="row g-40 mb-40">
                        {/* Brand & Dev Info */}
                        <div className="col-lg-5">
                            <div className="d-flex align-items-center gap-12 mb-16">
                                <img src="/logo192.png" alt="Catálogo App" className="w-36 h-36 object-fit-contain" />
                                <span className="fw-bold text-white text-lg">Catálogo App</span>
                            </div>
                            <p className="text-gray-400 text-sm mb-20 max-w-400">
                                {t('landing.footer.desc')}
                            </p>
                            <div className="text-xs text-gray-500">
                                {t('landing.footer.madeWith')} <span className="text-white fw-semibold">S7even Software</span>
                            </div>
                        </div>

                        {/* Quick Navigation Links */}
                        <div className="col-lg-3 col-6">
                            <h6 className="text-white text-sm fw-bold mb-16 text-uppercase tracking-wider">{t('landing.footer.quickLinks')}</h6>
                            <ul className="list-unstyled d-flex flex-column gap-8 text-sm p-0 m-0">
                                <li>
                                    <Link to="/admin/login" className="text-gray-400 hover-text-white text-decoration-none transition-2">
                                        {t('landing.footer.vendorPanel')}
                                    </Link>
                                </li>
                                <li>
                                    <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover-text-white text-decoration-none transition-2">
                                        {t('landing.footer.downloadApp')}
                                    </a>
                                </li>
                                <li>
                                    <Link to="/terms" className="text-gray-400 hover-text-white text-decoration-none transition-2">
                                        {t('landing.footer.terms')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/deleteAccount" className="text-gray-400 hover-text-white text-decoration-none transition-2">
                                        {t('landing.footer.deleteAccount')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Contact Details & Support */}
                        <div className="col-lg-4 col-6">
                            <h6 className="text-white text-sm fw-bold mb-16 text-uppercase tracking-wider">{t('landing.footer.contactSupport')}</h6>
                            <ul className="list-unstyled d-flex flex-column gap-12 text-sm p-0 m-0">
                                <li className="d-flex align-items-center gap-10">
                                    <i className="ph ph-envelope-simple text-main-400 text-lg"></i>
                                    <span>{t('landing.footer.support')}: <a href="mailto:s7evensoftware@gmail.com" className="text-gray-300 hover-text-white text-decoration-none">s7evensoftware@gmail.com</a></span>
                                </li>
                                <li className="d-flex align-items-center gap-10">
                                    <i className="ph ph-code text-main-400 text-lg"></i>
                                    <span>{t('landing.footer.developer')}: <span className="text-gray-300">Steven Jocol / S7even Software</span></span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-24 border-top border-gray-900 text-center text-xs text-gray-500 flex-between flex-wrap gap-12">
                        <span>© {new Date().getFullYear()} Catálogo App. {t('landing.footer.rights')}</span>
                        <div className="d-flex align-items-center gap-16">
                            <Link to="/terms" className="text-gray-500 hover-text-white text-decoration-none">{t('landing.footer.shortTerms')}</Link>
                            <span>•</span>
                            <Link to="/deleteAccount" className="text-gray-500 hover-text-white text-decoration-none">{t('landing.footer.deleteAccount')}</Link>
                            <span>•</span>
                            <Link to="/admin/login" className="text-gray-500 hover-text-white text-decoration-none">{t('landing.footer.shortVendorAccess')}</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePageOne;
